import { ChangeEvent, createElement, memo, useMemo, useState } from "react";
import i18n from "i18next";

import { logAnalyticsEvent, setAnalyticsUserProperties } from "@/constants/amplitude";
import { EventResultTypes, WebEventProps, WebEventTypes } from "@/constants/event";
import { sendMetrics } from "@/constants/gtag";
import { QuestionComponentProps } from "@/types/common";
import { formatDate } from "@/utils/dateFormatter";

import Button from "../../common/Button";
import useAnswers from "../../utils/useAnswers";

export const getDateOneMonthFromNow = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());
};

const DateQuestion = ({ question, handleNext }: QuestionComponentProps) => {
    const { answers } = useAnswers();

    const [selectedDate, setSelectedDate] = useState<Date>(
        (answers[question.id] as Date) ?? (question?.value as Date)
    );

    const titleElement = useMemo(() => {
        const title = question?.title;

        if (typeof title === "function") {
            return createElement(title, { eventName: question.eventName });
        }

        return title;
    }, [question]);

    const handleContinue = () => {
        const accepted = handleNext({ [question.id]: selectedDate });

        if (accepted) {
            logAnalyticsEvent(WebEventTypes.RESULT_EVENT, {
                [WebEventProps.SCREEN]: question.event,
                [WebEventProps.RESULT]: selectedDate.toISOString(),
            });
            setAnalyticsUserProperties({ [question.id]: selectedDate.toISOString() });
            sendMetrics(WebEventTypes.RESULT_EVENT, {
                [WebEventProps.SCREEN]: question.event,
                [WebEventProps.RESULT]: selectedDate,
            });
        }
    };

    const handleInputDate = (e: ChangeEvent<HTMLInputElement>) => {
        setSelectedDate(new Date(e.target.value) || new Date());
    };

    const handleSkip = () => {
        const accepted = handleNext({ [question.id]: EventResultTypes.SKIP });
        if (accepted) {
            logAnalyticsEvent(WebEventTypes.RESULT_EVENT, {
                [WebEventProps.SCREEN]: question.event,
                [WebEventProps.RESULT]: EventResultTypes.SKIP,
            });
        }
    };

    return (
        <div className="flex h-full flex-col gap-6 sm:justify-between">
            <div className="grid gap-6">
                <div className="whitespace-pre-line text-zing-24-32 font-semibold text-white female:text-black">
                    {titleElement}
                </div>
                {question?.subtitle && <div>{question?.subtitle}</div>}
                <input
                    type="date"
                    className="block w-[20vw] rounded-2xl border border-zing-gray-400 bg-black px-2 py-3 text-center text-zing-40-48 font-semibold leading-10 text-white placeholder-zing-gray-400 outline-none focus:border-zing-blue-200 female:border-zing-neutral-cool-grey-400 female:bg-transparent female:text-black female:placeholder-zing-neutral-cool-grey-400 female:focus:border-zing-warm-tangerine-400"
                    style={{ colorScheme: "dark" }}
                    min={question?.minDate}
                    max={question?.maxDate}
                    onChange={handleInputDate}
                    defaultValue={
                        answers[question.id] !== "skip"
                            ? formatDate((answers[question.id] ?? question?.value) as Date, {
                                  format: "yyyy-MM-dd",
                              }) ?? ""
                            : ""
                    }
                />
            </div>
            <div className="grid gap-2 pb-6 md:pb-10">
                <Button
                    onClick={handleContinue}
                    isDisabled={!selectedDate}
                    className="justify-self-center"
                >
                    <span className="normal-case">{question?.btnText || `${i18n.t("next")}`}</span>
                </Button>
                {!question?.notSkippable && (
                    <button
                        className="w-full rounded-2xl py-4 text-zing-16-22 font-semibold text-white female:text-black"
                        onClick={handleSkip}
                    >
                        {i18n.t("skip")}
                    </button>
                )}
            </div>
        </div>
    );
};

export default memo(DateQuestion);
